import { getSignInUrl } from "@app/config/getServiceUrl";
import { adminQueries, membershipQueries } from "@app/config/queryFactory";
import { cn } from "@base/lib/utils";
import { Avatar } from "@base/ui/Avatar";
import { Button } from "@base/ui/Button/Button";
import { Dropdown } from "@base/ui/Dropdown";
import { fetchUserInfo, logout } from "@features/auth/api/request";
import { LanguageDropdown } from "@features/language/ui";
import { mdiAccountCircleOutline, mdiChevronRight, mdiClose, mdiLogout, mdiMenu } from "@mdi/js";
import Icon from "@mdi/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Fragment, useState } from "react";
import type { PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

type Props = {
  className?: string;
};

export function BaseLayout({ className, children }: PropsWithChildren<Props>) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { data: userInfo } = useQuery({
    queryKey: ["userInfo"],
    queryFn: fetchUserInfo,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const { data: membership } = useQuery({
    ...membershipQueries.membership(),
    enabled: !!userInfo,
  });

  const { data: adminStatus } = useQuery({
    ...adminQueries.status(),
    enabled: !!userInfo,
  });

  const { i18n, t } = useTranslation();

  const currentLanguage = i18n.language;

  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userInfo"], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: membershipQueries.all(), refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: adminQueries.all(), refetchType: "all" });
      toast.success(t("loggedOutSuccessfully"));
      setIsMenuOpen(false);
      window.location.href = "/";
    },
  });

  return (
    <Fragment>
      <header
        className={cn(
          "flex fixed top-0 right-0 left-0 justify-between items-center px-6 py-4 bg-white shadow-sm z-globalHeader h-[58px] md:h-[80px]",
          className,
        )}
      >
        <Link to="/" className="flex items-center">
          <img src="/assets/82Office_Logo.svg" alt="82 Office" className="h-[34px] md:h-12 w-auto" />
        </Link>

        {/* Mobile navigation */}
        <div className="md:hidden">
          <button type="button" className="flex" onClick={() => setIsMenuOpen(true)}>
            <Icon path={mdiMenu} size="24px" />
          </button>
        </div>

        {/* Desktop navigation */}
        <div className="hidden gap-2 items-center md:flex">
          {/* 관리자는 회의실을 예약하는 주체가 아니므로 예약 진입점을 노출하지 않는다. */}
          {membership &&
            !adminStatus?.admin &&
            membership.status === "ACTIVE" &&
            membership.plan_description.type === "SILVER" && (
              <Button variant="ghost" className="font-semibold" asChild>
                <Link to="/reserve">{t("reserveRooms")}</Link>
              </Button>
            )}
          {(!userInfo || !membership || membership.status !== "ACTIVE") && (
            <Button
              variant="ghost"
              className="font-semibold"
              onClick={() => {
                if (userInfo !== undefined) {
                  navigate({ to: "/apply" });
                  return;
                }

                location.href = getSignInUrl({ locale: currentLanguage, redirectUrl: window.location.href });
              }}
            >
              {t("membershipApplyNow")}
            </Button>
          )}
          {userInfo && (
            <Dropdown>
              <Dropdown.Trigger asChild>
                <button type="button" className="flex gap-2 items-center">
                  <Avatar
                    fallback={userInfo.user.last_name.charAt(0).toUpperCase()}
                    className="w-8 h-8 bg-primary-500"
                  />
                  <span className="text-body2 text-gray-1000">{userInfo.user.name}</span>
                </button>
              </Dropdown.Trigger>
              <Dropdown.Content className="p-1 z-dropdown">
                <Dropdown.Item
                  value="my-page"
                  className="flex gap-2 items-center px-3 py-2 cursor-pointer rounded-4 text-body3 text-gray-1000 hover:bg-gray-100"
                  onClick={() => navigate({ to: "/myinfo" })}
                >
                  <Icon path={mdiAccountCircleOutline} size="1.125rem" className="shrink-0" />
                  {t("myPage")}
                </Dropdown.Item>
                <Dropdown.Item
                  value="logout"
                  className="flex gap-2 items-center px-3 py-2 cursor-pointer rounded-4 text-body3 text-gray-1000 hover:bg-gray-100"
                  onClick={() => mutate()}
                >
                  <Icon path={mdiLogout} size="1.125rem" className="shrink-0" />
                  {t("logout")}
                </Dropdown.Item>
              </Dropdown.Content>
            </Dropdown>
          )}
          <LanguageDropdown />
        </div>
      </header>

      {/* Mobile Drawer */}
      <div className="md:hidden">
        {/* Drawer panel */}
        <div
          className={cn(
            "flex fixed top-0 right-0 flex-col w-full h-full bg-white shadow-xl transition-transform duration-300 ease-in-out z-globalHeader",
            isMenuOpen ? "translate-x-0" : "translate-x-full",
          )}
        >
          {/* Drawer header */}
          <div className="flex items-center justify-between px-6 h-[58px] border-b border-gray-100">
            <span className="text-subtitle2 text-primary-500">82 Office</span>
            <button type="button" className="flex" onClick={() => setIsMenuOpen(false)}>
              <Icon path={mdiClose} size="24px" />
            </button>
          </div>

          {/* Drawer content */}
          <div className="flex overflow-y-auto flex-col flex-1 justify-between p-6">
            <div>
              {userInfo && (
                <>
                  <div className="flex gap-3 justify-between items-center px-2">
                    <div className="flex-1">
                      <span className="font-normal text-subtitle2">Welcome!</span>
                      <br />
                      <span className="text-subtitle2">{userInfo.user.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        navigate({ to: "/myinfo" });
                        setIsMenuOpen(false);
                      }}
                    >
                      <Icon path={mdiChevronRight} size="24px" />
                    </button>
                  </div>

                  <hr className="my-4 border-gray-500" />
                </>
              )}

              {/* 관리자는 회의실을 예약하는 주체가 아니므로 예약 진입점을 노출하지 않는다. */}
              {membership &&
                !adminStatus?.admin &&
                membership.status === "ACTIVE" &&
                membership.plan_description.type === "SILVER" && (
                  <button
                    type="button"
                    className="px-2 py-3 w-full text-left text-subtitle2 text-primary-500"
                    onClick={() => {
                      navigate({ to: "/reserve" });
                      setIsMenuOpen(false);
                    }}
                  >
                    {t("reserveRooms")}
                  </button>
                )}
              {(!userInfo || !membership || membership.status !== "ACTIVE") && (
                <button
                  type="button"
                  className="px-2 py-3 w-full text-left text-subtitle2 text-primary-500"
                  onClick={() => {
                    if (userInfo !== undefined) {
                      navigate({ to: "/apply" });
                      setIsMenuOpen(false);
                      return;
                    }

                    location.href = getSignInUrl({ locale: currentLanguage, redirectUrl: window.location.href });
                  }}
                >
                  {t("membershipApplyNow")}
                </button>
              )}
            </div>

            <div className="flex justify-between items-center">
              <LanguageDropdown />

              {userInfo !== undefined ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    mutate();
                  }}
                  isLoading={isPending}
                >
                  {t("logout")}
                </Button>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => {
                    location.href = getSignInUrl({ locale: currentLanguage, redirectUrl: window.location.href });
                  }}
                >
                  {t("signIn")}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <main className="pt-[58px] md:pt-[80px]">{children}</main>
    </Fragment>
  );
}
